import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUp,
  FiCheck,
  FiChevronDown,
  FiCpu,
  FiLink,
  FiMail,
  FiMapPin,
  FiMessageCircle,
  FiSearch,
  FiX,
} from "react-icons/fi";
import Footer from "../Footer/FooterPrice";
import FaqVideo from "./FaqVideo";
import { clipFor } from "./faqClips";
import { FAQ_TOPICS, FaqItem, FaqTag, POPULAR_QUESTION_IDS } from "./faqContent";

const DISCORD_URL = "https://discord.gg/UQmQw3bnhs";

const ALL_ITEMS = FAQ_TOPICS.flatMap((topic) => topic.items);
const ITEM_IDS = new Set(ALL_ITEMS.map((item) => item.id));

const TAG_STYLES: Record<FaqTag, string> = {
  App: "border-sky-400/30 text-sky-300",
  Website: "border-indigo-400/30 text-indigo-300",
  "All plans": "border-emerald-400/30 text-emerald-300",
  "Lite & Pro": "border-amber-400/30 text-amber-300",
  Pro: "border-violet-400/30 text-violet-300",
  Owners: "border-white/15 text-slate-300",
  Employees: "border-white/15 text-slate-300",
};

const searchText = (item: FaqItem, topicTitle: string) =>
  [
    topicTitle,
    item.question,
    item.answer,
    item.where,
    item.keywords,
    ...(item.steps ?? []),
    ...(item.details ?? []),
    ...(item.tags ?? []),
  ]
    .join(" ")
    .toLowerCase();

const scrollToId = (id: string) =>
  document.getElementById(id)?.scrollIntoView?.({ behavior: "smooth", block: "start" });

/** FAQPage structured data, so search engines can show answers directly. */
const useFaqStructuredData = () => {
  React.useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: ALL_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: [item.answer, ...(item.steps ?? [])].join(" "),
        },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, []);
};

const QuestionCard = ({
  item,
  open,
  onToggle,
}: {
  item: FaqItem;
  open: boolean;
  onToggle: (id: string) => void;
}) => {
  const [copied, setCopied] = React.useState(false);
  const clip = clipFor(item.clip);

  const copyLink = async () => {
    const url = `${window.location.origin}/faq#${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.hash = item.id;
    }
  };

  return (
    <div
      id={item.id}
      className={`scroll-mt-28 rounded-2xl border transition ${
        open
          ? "border-blue-400/30 bg-white/[0.05]"
          : "border-white/10 bg-white/[0.02] hover:border-white/20"
      }`}
    >
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${item.id}-answer`}
          onClick={() => onToggle(item.id)}
          className="flex w-full items-center gap-4 px-5 py-4 text-left"
        >
          <span className="flex-1 text-[15px] font-semibold text-white">{item.question}</span>
          <FiChevronDown
            aria-hidden
            className={`shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`}
          />
        </button>
      </h3>
      {open && (
        <div id={`${item.id}-answer`} className="px-5 pb-5">
          <div className={clip ? "grid gap-6 md:grid-cols-[minmax(0,1fr)_220px]" : ""}>
            <div className="min-w-0">
              <p className="text-[15px] leading-7 text-slate-300">{item.answer}</p>

              {item.steps && (
                <ol className="mt-4 space-y-2">
                  {item.steps.map((step, index) => (
                    <li key={step} className="flex gap-3 text-sm leading-6 text-slate-300">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-[11px] font-semibold text-blue-300">
                        {index + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              )}

              {item.details && (
                <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <FiCpu aria-hidden />
                    Technical details
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {item.details.map((detail) => (
                      <li key={detail} className="flex gap-2 text-sm leading-6 text-slate-400">
                        <span aria-hidden className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-slate-500" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {item.where && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-xs text-slate-300">
                    <FiMapPin aria-hidden size={12} />
                    {item.where}
                  </span>
                )}
                {item.tags?.map((tag) => (
                  <span
                    key={tag}
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${TAG_STYLES[tag]}`}
                  >
                    {tag}
                  </span>
                ))}
                <button
                  type="button"
                  onClick={copyLink}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-slate-400 transition hover:bg-white/5 hover:text-white"
                >
                  {copied ? <FiCheck aria-hidden size={12} /> : <FiLink aria-hidden size={12} />}
                  {copied ? "Link copied" : "Copy link"}
                </button>
              </div>
            </div>
            {clip && <FaqVideo clip={clip} />}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileAppFAQ: React.FC = () => {
  const [query, setQuery] = React.useState("");
  const [openIds, setOpenIds] = React.useState<Set<string>>(() => new Set());
  useFaqStructuredData();

  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searching = tokens.length > 0;

  const visibleTopics = FAQ_TOPICS.map((topic) => ({
    ...topic,
    items: topic.items.filter((item) => {
      const text = searchText(item, topic.title);
      return tokens.every((token) => text.includes(token));
    }),
  })).filter((topic) => topic.items.length > 0);
  const matchCount = visibleTopics.reduce((sum, topic) => sum + topic.items.length, 0);

  const toggle = (id: string) =>
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // /faq#<question> opens that answer; /faq#<topic> jumps to the topic.
  React.useEffect(() => {
    const openFromHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      if (ITEM_IDS.has(id)) {
        setQuery("");
        setOpenIds((current) => new Set(current).add(id));
      }
      window.setTimeout(() => scrollToId(id), 50);
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  return (
    <div className="bg-[#070b17] text-white">
      <section
        id="faq-top"
        className="relative isolate overflow-hidden px-5 pb-14 pt-28 md:px-8 md:pb-20 md:pt-36"
      >
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(rgba(148,163,184,0.16)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]"
        />
        <div
          aria-hidden
          className="absolute -top-48 left-1/2 -z-10 h-[520px] w-[760px] -translate-x-1/2 rounded-full bg-blue-600/25 blur-[130px]"
        />

        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Help center
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight md:text-6xl">
            How can we{" "}
            <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-violet-400 bg-clip-text text-transparent">
              help?
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-400">
            Straight answers about selling, stock, payments, staff and billing,
            with the technical details when you want them.
          </p>

          <div className="mx-auto mt-8 flex max-w-xl items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 shadow-lg shadow-black/30 focus-within:border-blue-400/50">
            <FiSearch aria-hidden className="shrink-0 text-slate-400" size={20} />
            <input
              type="search"
              aria-label="Search the FAQ"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search: offline, refunds, Square, staff pay…"
              className="min-w-0 flex-1 bg-transparent text-base text-white outline-none placeholder:text-slate-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10"
              >
                <FiX />
              </button>
            )}
          </div>

          {!searching && (
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {POPULAR_QUESTION_IDS.map((id) => {
                const item = ALL_ITEMS.find((candidate) => candidate.id === id);
                return item ? (
                  <a
                    key={id}
                    href={`#${id}`}
                    className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-sm text-slate-300 transition hover:border-white/25 hover:text-white"
                  >
                    {item.question}
                  </a>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 pb-24 md:px-8">
        {!searching && (
          <div className="mb-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FAQ_TOPICS.map((topic) => {
              const Icon = topic.icon;
              return (
                <a
                  key={topic.id}
                  href={`#${topic.id}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition hover:border-blue-400/40 hover:bg-white/[0.05]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                    <Icon aria-hidden size={18} />
                  </span>
                  <p className="mt-3 font-semibold text-white">{topic.title}</p>
                  <p className="mt-1 text-sm leading-5 text-slate-400">{topic.blurb}</p>
                </a>
              );
            })}
          </div>
        )}

        <div className="grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <nav aria-label="FAQ topics" className="sticky top-28 space-y-1">
              {visibleTopics.map((topic) => (
                <a
                  key={topic.id}
                  href={`#${topic.id}`}
                  className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-slate-400 transition hover:bg-white/5 hover:text-white"
                >
                  {topic.title}
                  <span className="text-xs text-slate-600">{topic.items.length}</span>
                </a>
              ))}
            </nav>
          </aside>

          <main className="min-w-0 space-y-14">
            {searching && (
              <p role="status" className="text-sm text-slate-400">
                {matchCount} {matchCount === 1 ? "answer" : "answers"} for “{query.trim()}”
              </p>
            )}
            {searching && matchCount === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center">
                <h2 className="text-lg font-semibold">No answers match that</h2>
                <p className="mt-2 text-sm text-slate-400">
                  Try a different word, or ask us at{" "}
                  <a className="text-blue-300 hover:underline" href="mailto:support@vendororbis.com">
                    support@vendororbis.com
                  </a>
                  .
                </p>
              </div>
            )}

            {visibleTopics.map((topic) => {
              const Icon = topic.icon;
              return (
                <section key={topic.id} id={topic.id} className="scroll-mt-28">
                  <div className="mb-5 flex items-start gap-3">
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                      <Icon aria-hidden size={20} />
                    </span>
                    <div>
                      <h2 className="text-2xl font-bold tracking-tight">{topic.title}</h2>
                      <p className="mt-1 text-sm text-slate-400">{topic.blurb}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {topic.items.map((item) => (
                      <QuestionCard
                        key={item.id}
                        item={item}
                        open={searching || openIds.has(item.id)}
                        onToggle={toggle}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-blue-600/20 via-indigo-600/10 to-violet-600/20 p-8 md:p-10">
              <h2 className="text-2xl font-bold">Still stuck?</h2>
              <p className="mt-2 max-w-xl text-slate-300">
                Tell us the screen, what you were doing and what you expected.
                A screenshot helps a lot.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="mailto:support@vendororbis.com"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-200"
                >
                  <FiMail aria-hidden />
                  support@vendororbis.com
                </a>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  <FiMessageCircle aria-hidden />
                  Discord community
                </a>
                <Link
                  to="/support"
                  className="inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold text-slate-300 transition hover:text-white"
                >
                  Support page
                </Link>
              </div>
            </section>

            <a
              href="#faq-top"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"
            >
              <FiArrowUp aria-hidden />
              Back to top
            </a>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MobileAppFAQ;
